// Чистые хелперы роль-слотов промпта.

import type { ImageInputRole } from "~/shared/data/chat-models";
import type { PendingUpload } from "~/shared/store/types";

/** Занятое вложение по каждой роли (первое с этой ролью — слот держит одно). */
export function itemsByRole(
    items: readonly PendingUpload[],
    roles: readonly ImageInputRole[],
): Partial<Record<ImageInputRole, PendingUpload>> {
    const map: Partial<Record<ImageInputRole, PendingUpload>> = {};
    for (const role of roles) {
        const found = items.find((item) => item.role === role);
        if (found) map[role] = found;
    }
    return map;
}

export interface RoleAssignmentPlan {
    /** Каким вложениям проставить роль (в порядке ролей режима). */
    assign: { localId: string; role: ImageInputRole }[];
    /** Что убрать: картинок больше, чем у режима ролей. */
    remove: PendingUpload[];
}

/**
 * План перевода плоского пула картинок в роль-слоты при переключении режима.
 *
 * Пул упорядочен пользователем (превью с drag-and-drop), поэтому порядок и есть
 * его намерение: первая картинка → первая роль. Лишние сверх числа ролей
 * убираем — молча отправить их некуда, роли для них нет.
 * Уже размеченные вложения не трогаем (их роль — явный выбор в слоте).
 */
export function planRoleAssignment(
    items: readonly PendingUpload[],
    roles: readonly ImageInputRole[],
): RoleAssignmentPlan {
    const images = items.filter((item) => item.kind === "image");
    const taken = new Set(images.filter((item) => item.role).map((item) => item.role));
    const freeRoles = roles.filter((role) => !taken.has(role));
    const unassigned = images.filter((item) => !item.role);

    return {
        assign: unassigned
            .slice(0, freeRoles.length)
            .map((item, index) => ({ localId: item.localId, role: freeRoles[index]! })),
        remove: unassigned.slice(freeRoles.length),
    };
}

/**
 * Заполнены ли первые `requiredCount` ролей подряд.
 *
 * Именно подряд: заполненный второй кадр без первого — не «одна картинка есть»,
 * а неоднозначная раскладка, которую нельзя отправлять (см. `orderImageUrlsByRole`).
 */
export function hasRequiredRoles(
    byRole: Partial<Record<ImageInputRole, PendingUpload>>,
    roles: readonly ImageInputRole[],
    requiredCount: number,
): boolean {
    return roles.slice(0, requiredCount).every((role) => byRole[role]?.status === "done");
}
